/**
 * The deep link the auth guard bounced someone off, kept so sign-in can
 * resume it instead of defaulting to home.
 *
 * The defect this closes: a signed-out person opening an invite link was
 * redirected to welcome with the URL discarded, and after signing in the
 * guard routed to home — the invitation silently evaporated, on web and on a
 * native cold start alike. The people opening invite links are exactly the
 * people least likely to be signed in.
 *
 * One slot, in memory, with sessionStorage mirroring on web so the popup
 * OAuth round-trip and an accidental reload both survive. Deliberately NOT
 * persistent storage: a destination remembered across days would teleport
 * someone into a stale invitation long after they stopped meaning to open
 * it. Zero imports so the root `node --test` suites can execute it.
 */

/**
 * Only destinations that are themselves invitations are worth resuming: they
 * carry one-shot intent that is lost otherwise. Everything else a signed-out
 * visitor lands on (a chat they cannot see, a tab) has a home in the app's
 * ordinary navigation and resuming it would be surprising, not helpful.
 */
export function isResumableDestination(path: string): boolean {
  return /^\/invite\/[^/]+$/.test(path) || /^\/group\/join\/[^/]+$/.test(path);
}

const STORAGE_KEY = "opncue.pendingDestination";

let pending: string | null = null;

function storage(): Storage | null {
  // Guarded access: absent on native, and the accessor itself can throw in
  // browsers set to block site data.
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function rememberDestination(path: string): void {
  if (!isResumableDestination(path)) return;
  pending = path;
  try {
    storage()?.setItem(STORAGE_KEY, path);
  } catch {
    // Memory still holds it for this JS session.
  }
}

/** The remembered destination, without consuming it. */
export function peekDestination(): string | null {
  if (pending) return pending;
  try {
    const stored = storage()?.getItem(STORAGE_KEY) ?? null;
    // Re-validated on the way out: sessionStorage outlives deploys, and a
    // value written by older code must not become a redirect target just
    // because it was stored.
    return stored && isResumableDestination(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function clearDestination(): void {
  pending = null;
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — peek re-validates anyway.
  }
}
